import { uniqueBy } from 'lib/array/arrayUtils'
import { isRecord } from 'lib/object/objectUtils'

import {
  LOYALTY_OFFERS_HUB_ALL_INDUSTRY_FILTER,
  LoyaltyOffersHubOfferTag,
} from './constants'
import {
  mapLoyaltyHeroCarouselItem,
  mapLoyaltyOffer,
} from './mapLoyaltyOffersHubRemoteConfig'
import type {
  LoyaltyOffersHubIndustryFilterOption,
  LoyaltyOffersHubOffer,
  LoyaltyOfferWithIndustryFilter,
  LoyaltyOffersHubViewModel,
} from './types'

const EMPTY_LOYALTY_OFFERS_HUB_VIEW_MODEL: LoyaltyOffersHubViewModel = {
  heroCarouselItems: [],
  partnerOffers: [],
  limitedTimeOffers: [],
  industryFilters: [LOYALTY_OFFERS_HUB_ALL_INDUSTRY_FILTER],
  hasActiveContent: false,
  hasResolvedRemoteConfig: false,
}

function hasIndustryFilter(offer: LoyaltyOffersHubOffer): offer is LoyaltyOfferWithIndustryFilter {
  return !!offer.industry && !!offer.industryLabel
}

function deriveIndustryFilters(partnerOffers: Array<LoyaltyOffersHubOffer>): Array<LoyaltyOffersHubIndustryFilterOption> {
  return [
    LOYALTY_OFFERS_HUB_ALL_INDUSTRY_FILTER,
    ...uniqueBy(
      partnerOffers.filter(hasIndustryFilter),
      offer => offer.industry,
    ).map((offer) => ({
      industry: offer.industry,
      label: offer.industryLabel,
    })),
  ]
}

export function resolveLoyaltyOffersHubRemoteConfig(
  remoteConfig: App.LabRemoteConfigResult | undefined,
  regionCode: string,
  now = new Date(),
): LoyaltyOffersHubViewModel {
  const { value } = remoteConfig ?? {}
  if (!isRecord(value)) {
    return {
      ...EMPTY_LOYALTY_OFFERS_HUB_VIEW_MODEL,
      hasResolvedRemoteConfig: !!remoteConfig,
    }
  }

  const rawHeroCarouselItems = Array.isArray(value.heroCarouselItems) ? value.heroCarouselItems : []
  const rawOffers = Array.isArray(value.offers) ? value.offers : []

  const heroCarouselItems = rawHeroCarouselItems.reduce<LoyaltyOffersHubViewModel['heroCarouselItems']>((result, item) => {
    const heroCarouselItem = mapLoyaltyHeroCarouselItem(item, regionCode, now)
    if (heroCarouselItem) result.push(heroCarouselItem)
    return result
  }, [])

  const offers = rawOffers.reduce<Array<LoyaltyOffersHubOffer>>((result, item) => {
    const offer = mapLoyaltyOffer(item, regionCode, now)
    if (offer) result.push(offer)
    return result
  }, [])

  const partnerOffers = offers.filter(offer => offer.offerTag === LoyaltyOffersHubOfferTag.PARTNER_OFFER)
  const limitedTimeOffers = offers.filter(offer => offer.offerTag === LoyaltyOffersHubOfferTag.LUXURY_ESCAPES_OFFER)

  return {
    heroCarouselItems,
    partnerOffers,
    limitedTimeOffers,
    industryFilters: deriveIndustryFilters(partnerOffers),
    hasActiveContent: heroCarouselItems.length > 0 || offers.length > 0,
    hasResolvedRemoteConfig: true,
  }
}

export {
  LOYALTY_OFFERS_HUB_ALL_INDUSTRY_FILTER,
  LoyaltyOffersHubOfferTag,
  LoyaltyOffersHubUrgencyType,
} from './constants'
export type {
  LoyaltyOffersHubHeroCarouselItem,
  LoyaltyOffersHubIndustryFilter,
  LoyaltyOffersHubIndustryFilterOption,
  LoyaltyOffersHubOffer,
  LoyaltyOffersHubViewModel,
} from './types'
